import React, { useState, useEffect, Suspense } from 'react'
import _filter from 'lodash/filter'
import Div100vh from 'react-div-100vh'
import clsx from 'clsx'

import {
  makeStyles,
  createStyles,
  useMediaQuery,
  Grid,
  Container,
  ContainerProps,
} from '@material-ui/core'

import useRouter from 'hooks/useRouter'
import { useAppContext, hubModes } from 'contexts/AppContext'

import { db, auth } from '../../firebase'
import FireLoop from 'fireloop'
import {
  Loading,
  ErrorBoundary,
  EmptyState,
} from '@antlerengineering/components'
import TopBar from './TopBar'
import NavSidebar, { DRAWER_WIDTH } from './NavSidebar'

export const DESKTOP_NAV = '@media (min-width: 1024px)'
export const MOBILE_NAV = '@media (max-width: 1023.95px)'

const useStyles = makeStyles(theme =>
  createStyles({
    root: {},

    drawerGridItem: {
      overflow: 'hidden',
      height: '100%',
      [DESKTOP_NAV]: { width: DRAWER_WIDTH },
    },

    fullHeight: { height: '100%' },
    mainContainer: {
      padding: theme.spacing(2),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(3) },
      [theme.breakpoints.up('md')]: { padding: theme.spacing(4) },

      [DESKTOP_NAV]: { width: `calc(100vw - ${DRAWER_WIDTH}px)` },
    },
  })
)

interface NavigationProps {
  children?: any
  maxWidth?: ContainerProps['maxWidth']
}

const Navigation: React.FC<NavigationProps> = ({ children, maxWidth }) => {
  const classes = useStyles()
  const isMobile = useMediaQuery(MOBILE_NAV)
  const router = useRouter()

  // Store restrictions for nav items here. Used to disable or hide nav elements
  const [restrictions] = useState<string[]>(
    process.env.NODE_ENV === 'production' ? ['development'] : []
  )

  const {
    hubMode,
    userDocState,
    cohortCollectionState,
    currentUser,
  } = useAppContext()

  const [mobileOpen, setMobileOpen] = useState(false)
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen)
  useEffect(() => {
    if (mobileOpen) setMobileOpen(false)
  }, [router.location.pathname])

  if (userDocState.loading) return <Loading message="Loading user" fullScreen />
  if (!userDocState.doc)
    throw new Error(`User not found: ${currentUser?.email}`)
  if (!userDocState.doc.cohort && hubMode !== hubModes.internal)
    throw new Error(`User is not part of a cohort: ${currentUser?.email}`)

  // if (cohortCollectionState.loading)
  //   return <Loading message="Loading cohort" fullScreen />
  // if (!cohortCollectionState.documents[0])
  //   throw new Error(`Cohort not found: ${currentUser?.email}`)

  return (
    <>
      <Grid
        container
        className={classes.root}
        wrap="nowrap"
        component={Div100vh}
        style={{ minHeight: '100rvh' }}
      >
        <Grid item className={classes.drawerGridItem}>
          <NavSidebar
            hubMode={hubMode}
            isMobile={isMobile}
            restrictions={restrictions}
            mobileOpen={mobileOpen}
            onCloseDrawer={handleDrawerToggle}
          />
        </Grid>

        <Grid item xs>
          <Grid
            container
            wrap="nowrap"
            direction="column"
            className={classes.fullHeight}
          >
            <TopBar
              hubMode={hubMode}
              isMobile={isMobile}
              restrictions={restrictions}
              onDrawerToggle={handleDrawerToggle}
              maxWidth={maxWidth}
            />

            <Grid item xs>
              <ErrorBoundary>
                <Container
                  component="main"
                  className={clsx(classes.fullHeight, classes.mainContainer)}
                  maxWidth={maxWidth}
                >
                  <Suspense fallback={<Loading />}>
                    {cohortCollectionState.path === null ? (
                      <EmptyState
                        message="No cohort selected"
                        description="Please choose a cohort using the dropdown on the bottom left"
                      />
                    ) : cohortCollectionState.loading ? (
                      <Loading message="Loading cohort" fullScreen />
                    ) : (
                      children
                    )}
                  </Suspense>
                </Container>
              </ErrorBoundary>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <FireLoop collectionRef={db.collection('feedback')} auth={auth} />
    </>
  )
}

export default Navigation
